import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

/**
 * Panel para visualizar y reprocesar URLs de competencia pendientes de capturar.
 * Muestra cuántas investigaciones tienen `archivos` con URL string (no procesado)
 * y permite disparar el background scraping para capturarlas y subirlas a Cloudinary.
 */
export const ScrapePendientesPanel = ({ API }) => {
  const [pendientes, setPendientes] = useState(0);
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [reprocesando, setReprocesando] = useState(false);

  const cargarEstado = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const [pendRes, jobsRes] = await Promise.all([
        fetch(`${API}/api/investigaciones/scrape/pendientes`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        fetch(`${API}/api/investigaciones/scrape/jobs`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      const pendData = await pendRes.json();
      const jobsData = await jobsRes.json();
      setPendientes(pendData.pendientes || 0);
      setJobs(jobsData.jobs || []);
    } catch (e) {
      // silent
    }
    setLoading(false);
  }, [API]);

  useEffect(() => {
    cargarEstado();
    const t = setInterval(cargarEstado, 8000);
    return () => clearInterval(t);
  }, [cargarEstado]);

  const reprocesar = async () => {
    if (pendientes === 0) {
      toast.info('No hay URLs pendientes de capturar.');
      return;
    }
    if (!window.confirm(`Se capturarán screenshots de ${pendientes} URL(s) y se subirán a Cloudinary. ¿Continuar?`)) return;
    setReprocesando(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/api/investigaciones/scrape/reprocesar?limite=500`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`📸 ${data.queued} URLs en proceso. El panel se actualizará automáticamente.`);
        cargarEstado();
      } else {
        toast.error(data.error || 'Error reprocesando');
      }
    } catch (e) {
      toast.error('Error de conexión');
    }
    setReprocesando(false);
  };

  const activos = jobs.filter((j) => j.status === 'running');
  const recientes = jobs.slice(0, 3);

  return (
    <div className="mt-4 border-t-2 border-purple-100 pt-4" data-testid="scrape-pendientes-panel">
      <div className="flex items-center justify-between mb-2">
        <div>
          <h4 className="font-semibold text-purple-800 text-sm flex items-center gap-2">
            📸 Captura web de URLs de competencia
          </h4>
          <p className="text-xs text-purple-600 mt-0.5">
            Si el Excel tiene URLs en <code className="bg-purple-50 px-1 rounded">archivos</code>, el sistema captura screenshot y lo sube a Cloudinary automáticamente.
          </p>
        </div>
        <button
          onClick={reprocesar}
          disabled={loading || reprocesando || pendientes === 0}
          data-testid="reprocesar-urls-btn"
          className="px-3 py-1.5 text-xs font-medium rounded-md bg-purple-600 text-white hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
        >
          {reprocesando ? '⏳ Encolando...' : `Reprocesar URLs pendientes (${pendientes})`}
        </button>
      </div>

      {activos.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-2 text-xs text-amber-800 mb-2" data-testid="scrape-job-activo">
          ⏳ <strong>{activos.length}</strong> job(s) en ejecución ·
          {' '}{activos.reduce((a, b) => a + b.ok, 0)} OK · {activos.reduce((a, b) => a + b.fail, 0)} fallidas
          {activos[0]?.last_url && <div className="truncate mt-1 italic">→ {activos[0].last_url}</div>}
        </div>
      )}

      {recientes.length > 0 && (
        <details className="text-xs text-gray-600">
          <summary className="cursor-pointer hover:text-purple-700">Ver últimos jobs ({recientes.length})</summary>
          <ul className="mt-1 space-y-1">
            {recientes.map((j) => (
              <li key={j.job_id} className="font-mono bg-gray-50 px-2 py-1 rounded">
                <span className={j.status === 'done' ? 'text-green-700' : 'text-amber-600'}>
                  {j.status === 'done' ? '✅' : '⏳'}
                </span>{' '}
                <span className="text-gray-500">{j.job_id}</span> · {j.ok}/{j.total} OK · {j.fail} fail
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
};

export default ScrapePendientesPanel;
